import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Stack,
  Typography,
} from "@mui/material";

const associationMembers = [
  {
    role: "President",
    image: "/images/AssociationMembers/President.webp",
    accent: "#1d4ed8",
    summary:
      "Guiding the association with strategic direction, partnerships, and long-term community growth.",
  },
  {
    role: "Secretary",
    image: "/images/AssociationMembers/Seceretary.webp",
    accent: "#ea580c",
    summary:
      "Coordinating communication, records, and operational rhythm for the association leadership team.",
  },
  {
    role: "Vice President",
    image: "/images/AssociationMembers/VicePresident.webp",
    accent: "#0f766e",
    summary:
      "Supporting cross-functional initiatives and strengthening collaboration across the alumni network.",
  },
  {
    role: "Vice President",
    image: "/images/AssociationMembers/VicePresident_2.webp",
    accent: "#7c3aed",
    summary:
      "Driving member engagement and helping convert ideas into structured association programs.",
  },

  {
    role: "Joint Secretary",
    image: "/images/AssociationMembers/JointSeceratery_1.webp",
    accent: "#2563eb",
    summary:
      "Supporting program execution and ensuring timely coordination across activities and committees.",
  },
  {
    role: "Joint Secretary",
    image: "/images/AssociationMembers/JointSec_2.webp",
    accent: "#0891b2",
    summary:
      "Helping streamline member outreach, planning, and association-wide communication efforts.",
  },
  {
    role: "Joint Secretary",
    image: "/images/AssociationMembers/JointSec_3.webp",
    accent: "#be123c",
    summary:
      "Contributing to event logistics, internal follow-through, and strong day-to-day team support.",
  },
  {
    role: "Treasurer",
    image: "/images/AssociationMembers/Treasurer.webp",
    accent: "#15803d",
    summary:
      "Overseeing financial stewardship with a focus on transparency, accountability, and sustainability.",
  },
];

const highlights = [
  "Leadership grounded in service and accountability",
  "Structured support for community, events, and outreach",
  "Focused on long-term alumni impact and collaboration",
];

function AssociationMembers() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(180deg, #f8fbff 0%, #eef4ff 35%, #ffffff 100%)",
        py: { xs: 6, md: 9 },
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            borderRadius: 6,
            px: { xs: 3, md: 6 },
            py: { xs: 4, md: 6 },
            mb: 5,
            background:
              "linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(29,78,216,0.95) 100%)",
            color: "white",
            boxShadow: "0 24px 60px rgba(37, 99, 235, 0.18)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -60,
              right: -40,
              width: 220,
              height: 220,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.08)",
            }}
          />
          <Chip
            label="Association Leadership"
            sx={{
              mb: 2,
              color: "white",
              bgcolor: "rgba(255,255,255,0.12)",
              fontWeight: 700,
            }}
          />
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              maxWidth: 680,
              fontSize: { xs: "2rem", md: "3rem" },
            }}
          >
            Meet the members guiding Einstein Seeds with purpose and
            professionalism.
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mt: 2,
              maxWidth: 760,
              color: "rgba(255,255,255,0.82)",
              lineHeight: 1.7,
              fontWeight: 400,
            }}
          >
            This leadership team supports member engagement, community programs,
            and the long-term direction of the association.
          </Typography>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            divider={
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  display: { xs: "none", md: "block" },
                  borderColor: "rgba(255,255,255,0.18)",
                }}
              />
            }
            sx={{ mt: 4 }}
          >
            {highlights.map((item) => (
              <Typography
                key={item}
                variant="body1"
                sx={{ color: "rgba(255,255,255,0.9)" }}
              >
                {item}
              </Typography>
            ))}
          </Stack>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(4, minmax(0, 1fr))",
            },
            gap: 3,
          }}
        >
          {associationMembers.map((member, index) => (
            <Card
              key={`${member.role}-${index}`}
              sx={{
                height: "100%",
                borderRadius: 5,
                overflow: "hidden",
                border: "1px solid rgba(148, 163, 184, 0.18)",
                boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
                transition: "transform 0.25s ease, box-shadow 0.25s ease",
                "&:hover": {
                  transform: "translateY(-6px)",
                  boxShadow: "0 24px 52px rgba(15, 23, 42, 0.14)",
                },
              }}
            >
              <Box sx={{ position: "relative" }}>
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: `linear-gradient(180deg, rgba(15,23,42,0) 45%, ${member.accent} 100%)`,
                    opacity: 0.92,
                  }}
                />
                <Box
                  component="img"
                  src={member.image}
                  alt={member.role}
                  sx={{
                    width: "100%",
                    aspectRatio: "4 / 4.6",
                    objectFit: "cover",
                    objectPosition: "top center",
                    display: "block",
                  }}
                />
                <Chip
                  label={member.role}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 16,
                    left: 16,
                    bgcolor: "rgba(255,255,255,0.9)",
                    color: "#0f172a",
                    fontWeight: 700,
                  }}
                />
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={800} color="#0f172a">
                  {member.role}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mt: 1.25,
                    color: "#475569",
                    lineHeight: 1.75,
                  }}
                >
                  {member.summary}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>
    </Box>
  );
}

export default AssociationMembers;
